/**
 * API-Client für die Channel-Content-Generierung (Optimierungen-Drawer, MVP).
 *
 * - generate-content: transiente Vorschläge je Feld (1 JSON-Generierung)
 * - apply (Übernehmen): reuse des bestehenden /descriptions/push (schreibt JTL + Version)
 * - recompute: Readiness/Score nach Übernahme neu berechnen
 *
 * Verträge eingefroren — siehe docs/superpowers/specs/2026-05-25-channel-content-MVP*.
 */

import apiClient from '../api-client'

/** Eine vom Backend gemeldete verletzte Regel ("warum"-Zeile). */
export interface TriggeredRule {
  field: string
  message: string
  actual?: string | null
  expected?: string | null
}

/** Vorschlag für genau ein Feld (transient, nicht persistiert). */
export interface ContentFieldSuggestion {
  attribute_code: 'title' | 'description' | 'short_description' | 'bullets'
  previous: string | string[] | null
  suggestion: string | string[]
  rule_compliant: boolean
  triggered_rules: TriggeredRule[]
}

export interface GenerateContentResponse {
  success: boolean
  credits: number
  fields: ContentFieldSuggestion[]
  error?: string | null
}

/** content_blocks block_type bzw. Bullet-Slot für den /push-Endpoint. */
export type PushBlockType =
  | 'title'
  | 'description'
  | 'short_description'
  | 'Bullet1'
  | 'Bullet2'
  | 'Bullet3'
  | 'Bullet4'
  | 'Bullet5'

const BASE = '/api/v1'

/**
 * Generiert Vorschläge für die angefragten Felder (leer = alle in-scope, proaktiv).
 * Zeilen-Button → ein Feld; Sammelknopf → die verletzenden/fehlenden Felder.
 */
export async function generateChannelContent(
  productId: string,
  channelId: string,
  params: { attribute_codes?: string[]; bullets_mode?: 'fill' | 'replace' } = {},
): Promise<GenerateContentResponse> {
  const res = await apiClient.post(
    `${BASE}/products/${productId}/channels/${channelId}/generate-content`,
    {
      attribute_codes: params.attribute_codes ?? [],
      bullets_mode: params.bullets_mode ?? 'fill',
    },
  )
  return res.data
}

/**
 * Übernehmen eines Feldwerts → bestehender JTL-Push (schreibt JTL + content_block-Version).
 * Bullets werden je Slot (Bullet1..Bullet5) einzeln gepusht.
 */
export async function applyContentField(
  productId: string,
  body: {
    sales_channel_id: string
    language_iso?: string
    block_type: PushBlockType
    content: string
  },
): Promise<{ success: boolean; error?: string }> {
  const res = await apiClient.post(`${BASE}/products/${productId}/descriptions/push`, {
    language_iso: 'de',
    ...body,
  })
  return res.data
}

/** Readiness/Score nach dem/den Push(es) neu berechnen. */
export async function recomputeChannel(
  productId: string,
  channelId: string,
): Promise<{ success: boolean }> {
  const res = await apiClient.post(
    `${BASE}/products/${productId}/channels/${channelId}/recompute`,
  )
  return res.data
}
